import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { LoggerService } from '@teammood/shared/util/logger';
import { FilterCodec } from "./codec/filter-codec.service";
import { AllowedOrgUnits, CompositeFilter, Predicate } from './filter.type';
import { AllFilters, FormatedSavedFilter, SavedFilter } from "./named-filter.type";

@Injectable({ providedIn: 'root' })
export class FiltersService {

    constructor(
        private _http: HttpClient,
        private _log: LoggerService,
        private _translate: TranslateService,
        private _filterCodec: FilterCodec
    ) { }
    
    async filters(onlyWhereIAMAdmin: boolean = false): Promise<AllFilters> {

        const params = onlyWhereIAMAdmin ?
            { params: new HttpParams().set('forAdminOnly', true) } : {};

        return await firstValueFrom(this._http.get<AllFilters>('/api/filter', params));
    }

    async createFilter(filter: SavedFilter): Promise<AllFilters>  {
        return await firstValueFrom(this._http.post<AllFilters>('/api/filter', {...filter}));
    }

    async updateFilter(filter: SavedFilter): Promise<AllFilters>  {
        return await firstValueFrom(this._http.post<AllFilters>('/api/filter', {...filter}));
    }

    async removeFilter(id: string): Promise<AllFilters> {
        return await firstValueFrom(this._http.delete<AllFilters>('/api/filter/' + id));
    }

    async duplicateFilter(id: string): Promise<AllFilters> {
        return await firstValueFrom(this._http.post<AllFilters>('/api/filter/duplicate/' + id, {}));
    }

    async setAsDefault(filterId: string): Promise<AllFilters> {
        return await firstValueFrom(this._http.put<AllFilters>('/api/filter/default/'+ filterId, {}));
    }

    async loadFilters(onlyWhereIAMAdmin: boolean = false): Promise<AllFilters> {
        try {
            return await this.filters(onlyWhereIAMAdmin);
        } catch (error) {
            this._log.error("filters loading error", error);
            if (error.status === 403) {
                alert('Well tried, but you are not authorized or you have to upgrade to the Premium plan');
            }
        }
    }

    getDefaultFilter(filters: SavedFilter[]): SavedFilter {
        const defaultFilter = filters.filter(filter => filter.default);
        if (!defaultFilter || defaultFilter.length === 0) {
            return filters[0];
        }
        return defaultFilter[0];
    }

    formatFilters(filters: AllFilters, create?:(filter: SavedFilter) => void, update?:(filter: SavedFilter) => void, addNewFilter: boolean = true): FormatedSavedFilter[] {
        let baseNamedFilters = [];
        let customNamedFilters = [];

        if(filters?.baseNamedFilters){
            baseNamedFilters = filters.baseNamedFilters.map(baseNamedFilter => {
                const decodedFilter: CompositeFilter = this._filterCodec.decode(baseNamedFilter.filter);
                let filterTypeLabelClass = '';
                switch (baseNamedFilter.type) {
                    case 'team':
                        filterTypeLabelClass = 'save-filter-label-team';
                        break;
                    case 'organization':
                        filterTypeLabelClass = 'save-filter-label-org';
                        break;
                }

                const filterTypeLabel = '<span class="save-filter-label '+filterTypeLabelClass+'">' + this._translate.instant(baseNamedFilter.type) + '</span>'

                return {
                    baseNamedFilter: true,
                    nameForSelector: '<span class="save-filter-label-wrapper"><span>'+ baseNamedFilter.name+ '</span>' + filterTypeLabel +'</span>',
                    ...baseNamedFilter,
                    decodedFilter: decodedFilter,
                    jsonFilter: JSON.stringify(decodedFilter),
                    onUpdate: update
                }
            });
        }

        if(filters?.customNamedFilters){
            customNamedFilters = filters.customNamedFilters.map(advancedNamedFilter => {
                const decodedFilter = this._filterCodec.decode(advancedNamedFilter.filter);
                const filterTypeLabel = '<span class="save-filter-label-custom"><i class="fa-light fa-plus"></i><i class="fa-light fa-filter"></i></span>'

                return {
                    baseNamedFilter: false,
                    nameForSelector: '<span class="save-filter-label-wrapper"><span>'+ advancedNamedFilter.name + '</span>'+ filterTypeLabel +'</span>',
                    ...advancedNamedFilter,
                    decodedFilter: decodedFilter,
                    jsonFilter: JSON.stringify(decodedFilter),
                    onUpdate: update
                }
            });
        }

        if(addNewFilter){
            return [
                ...baseNamedFilters,
                ...customNamedFilters,
                // empty filter for create mode
                {
                    default: false,
                    baseNamedFilter: false,
                    nameForSelector: '<span class="save-filter-label-wrapper"><span class="new-filter-link">'+ this._translate.instant('filter.new') + '</span></span>',
                    name: '',
                    createLink: true,
                    filter: undefined,
                    decodedFilter: {
                        predicates: []
                    },
                    onCreate: create,
                }
            ]
        }else{
            return [
                ...baseNamedFilters,
                ...customNamedFilters,
            ]
        }
    }

    getCustomFilters (filters: FormatedSavedFilter[]): FormatedSavedFilter[]{
        return filters.filter(namedFilter => !namedFilter.baseNamedFilter && !namedFilter.createLink)
    }


    arePredicatesAmongAllowedOrgUnits(predicates: Predicate[], allowedOrgUnit: AllowedOrgUnits[]): boolean {
        const recursiveResults = [];
        return this._arePredicatesAmongAllowedOrgUnits(recursiveResults, predicates, allowedOrgUnit)
    }

    private _arePredicatesAmongAllowedOrgUnits(recursiveResults, predicates, allowedOrgUnit) {
        if (predicates?.length > 0) {
            for (const predObj of predicates){
                if (predObj.predicates) {
                    const result = this._arePredicatesAmongAllowedOrgUnits(recursiveResults, predObj.predicates, allowedOrgUnit);
                    recursiveResults.push(result);
                }
                else if (predObj.org_unit_type) {
                    const sameTypeGroup = allowedOrgUnit.find(group => group.type === predObj.org_unit_type)
                    if(sameTypeGroup){
                        const result = !!sameTypeGroup.values.find(value => value.name === predObj.org_unit_value);
                        recursiveResults.push(result)
                    }else{
                        return false;
                    }
                }
            }
            return recursiveResults.reduce((acc, value) => acc && value, true) // refactor: do reduce instead for of
        }else{
            this._log.error("custom filter is empty - no predicates!");
            return false;
        }
    }
}